import {
  AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding,
  HostListener, Inject, Input, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, ViewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Product, ProductService } from '@spartacus/core';

import {
  combineLatest, Observable, of, Subject, catchError, filter, first, map, switchMap, take
} from 'rxjs';

import { SdProductSellerModalComponent } from '@seb/web-design-system/product-seller';
import { SdCrossSellComponent } from '@seb/web-design-system/cross-sell';
import {
  BrandsEnum, BrandThemesEnum, DeviceService, IProductCard, ISliderInfo, OrientationEnum, ScrollSpyService,
  SizesEnum, ThemesEnum
} from '@seb/web-design-system/core';
import { DynamicContentShowMore } from '@seb/web-design-system/show-more';

import {
  BaseStoreService, BundleService, CartFacadeService, CustomProductImageNormalizer, LazyLoadingService, StockService, TrackingService
} from '../../../services';
import { OmnibusRecommendedPriceModalComponent } from '../../static/omnibus-recommended-price-modal/omnibus-recommended-price-modal.component';
import { ProductNotificationModalComponent } from '../../static/product-notification-modal/product-notification-modal.component';
import { Bundle, CrossSellCodeInterface, EventProductOrBundle, productCardSkeleton } from '../../../models';
import { ProductScopeEnum, StockStatusEnum } from '../../../enums';
import { BundleMapper, ProductMapper } from '../../../mappers';

@Component({
  selector: 'wc-base-cross-sell',
  templateUrl: './base-cross-sell.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SdCrossSellComponent,
    SdProductSellerModalComponent,
    ProductNotificationModalComponent,
    OmnibusRecommendedPriceModalComponent
  ]
})
export class BaseCrossSellComponent implements AfterViewChecked, OnChanges, OnDestroy {
  @Input() class = '';

  @Input()
  codes: CrossSellCodeInterface;

  @Input()
  loadedProducts?: Product[];

  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  titleLevel: number;

  @Input()
  isEmbedded = false;

  @Input()
  showMoreDesktop = true;

  @Input()
  layout: OrientationEnum;

  @Input() isUnderlined: boolean;

  @Input() titleTheme = BrandThemesEnum.brand;

  @Input() background: ThemesEnum;

  @Input()
  isFirst = false;

  @Input()
  context: string;

  @Input()
  brandCorner = BrandsEnum.default;

  @Input()
  productCodeForAccessory: string;

  @Input()
  refreshPrices = true;

  @Input()
  dynamicContentShowMore: DynamicContentShowMore;

  @ViewChild('crossSellComponent', { read: ElementRef })
  crossSellComponent: ElementRef;

  @ViewChild('crossSellComponent')
  crossSellCmp: SdCrossSellComponent;

  @Output()
  nextPage = new EventEmitter();

  @Output()
  hasLoaded = new EventEmitter<void>();

  @Output()
  hasUpdatedLoadedProducts = new EventEmitter<boolean>();

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-base'].filter(Boolean).join(' ');
  }

  componentIsSeen = false;
  products: IProductCard[] = [];
  productsPerRow: number;
  notifyModalOpened = false;
  notifyProduct = '';
  notifyProductName = '';
  currentProducts: Product[] = [];
  currentBundles: Bundle[] = [];
  isOpenedSellerModal = false;
  size: SizesEnum.s | SizesEnum.m;
  sizesEnum = SizesEnum;
  orientationEnum = OrientationEnum;
  areProductsLoaded = false;
  useLazyLoad = false;
  isOpenedRecommendedModal = false;
  currentSwiperInfo: ISliderInfo;
  isMobile: boolean;
  isNewPriceManagementActivated: boolean;
  isSSR = true;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private productService: ProductService,
    private bundleService: BundleService,
    private productMapper: ProductMapper,
    private bundleMapper: BundleMapper,
    private cdRef: ChangeDetectorRef,
    private trackingService: TrackingService,
    private scrollSpyService: ScrollSpyService,
    private deviceService: DeviceService,
    private baseStoreService: BaseStoreService,
    private lazyLoadingService: LazyLoadingService,
    private customImageNormalizer: CustomProductImageNormalizer,
    private readonly cartFacadeService: CartFacadeService,
    private readonly stockService: StockService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.useLazyLoad = this.lazyLoadingService.isEnabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isTablet$: Observable<boolean> = this.deviceService.isTablet;
    const isMobile$: Observable<boolean> = this.deviceService.isMobile;
    combineLatest([isTablet$, isMobile$])
      .pipe(
        take(1),
        switchMap(([isTablet, isMobile]) => {
          let loadedProductCard$: Observable<IProductCard>[];
          if (changes['loadedProducts']?.currentValue) {
            loadedProductCard$ = this.loadedProducts
              .filter((loadedProduct) => loadedProduct?.price?.value > 0)
              .map((loadedProduct) => {
                if (Object.keys(loadedProduct).length > 0) {
                  loadedProduct.brand = this.brandCorner
                    ? this.brandCorner
                    : loadedProduct.brand
                      ? loadedProduct.brand
                      : BrandsEnum.default;
                  if (loadedProduct?.images?.length) {
                    loadedProduct.images = this.customImageNormalizer.normalize((loadedProduct as any)?.images);
                  }
                  return this.productMapper.productToProductCard(loadedProduct);
                } else {
                  return of({});
                }
              });
          } else {
            loadedProductCard$ = [];
          }
          return combineLatest([of(isTablet), of(isMobile), ...loadedProductCard$]);
        })
      )
      .subscribe(([isTablet, isMobile, ...loadedProductCard]: [boolean, boolean, IProductCard]) => {
        this.isMobile = isMobile;
        if (!isMobile && this.layout === OrientationEnum.row) {
          this.productsPerRow = 2;
        } else {
          if (isTablet) {
            this.productsPerRow = 2;
          } else if (isMobile) {
            this.productsPerRow = 1;
          } else {
            this.productsPerRow = 3;
          }
        }
        if (loadedProductCard?.length > 0) {
          this.products = loadedProductCard.map((loadedProduct) => ({
            ...loadedProduct,
            isProductComparator: false
          }));
          this.currentProducts = this.loadedProducts;
          this.updateProductCards();
          this.areProductsLoaded = true;
        }

        if (changes['codes']?.currentValue) {
          if (!this.showMoreDesktop) {
            this.areProductsLoaded = false;
            if (this.useLazyLoad) {
              this.products = new Array((this.codes.products?.length ?? 0) + (this.codes.bundles?.length ?? 0)).fill(undefined).map(
                (_, i) => ({ ...productCardSkeleton, id: `${i}` })
              );
            }
          }

          this.loadProductsAndBundles();
        }
        this.cdRef.markForCheck();
      });
  }

  ngAfterViewChecked() {
    this.crossSellCmp?.crossSellSlider?.refreshSlider();
  }

  onSwiperInfo(event: ISliderInfo): void {
    this.currentSwiperInfo = { index: event.index, itemsPerView: event.itemsPerView };
  }

  onSlidePrev(event: ISliderInfo): void {
    if (this.currentProducts) {
      this.activateCrossSellProductListTracking(event);
    }
  }

  onSlideNext(event: ISliderInfo): void {
    if (this.currentProducts) {
      this.activateCrossSellProductListTracking(event);
    }
    this.nextPage.emit(event.itemsPerView);
  }

  addToCart(productCode: string): void {
    sessionStorage.setItem('addToCartContext', this.context);
    const currentBundle: Bundle = this.currentBundles.find((bundle) => bundle.id === productCode);
    const currentProduct: Product = this.currentProducts.find((product) => product.code === productCode);
    if (currentProduct) {
      this.cartFacadeService.addToCartShippingType(currentProduct, 1);
      this.hasUpdatedLoadedProducts.emit(true);
    }
    if (currentBundle) {
      this.cartFacadeService.addBundleToCartShippingType(currentBundle, 1);
      this.hasUpdatedLoadedProducts.emit(true);
    }
  }

  notifyMe(productCode: string): void {
    const currentProduct: Product = this.currentProducts.find((product) => product.code === productCode);
    this.notifyProductName = currentProduct.name;
    this.notifyProduct = productCode;
    this.notifyModalOpened = true;
  }

  openSellerModal(): void {
    this.isOpenedSellerModal = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onProductClick(productCodeClicked: string): void {
    const currentProductIndex = this.currentProducts.findIndex((product) => product.code === productCodeClicked);
    const currentProduct: Product = this.currentProducts[currentProductIndex];
    if (currentProduct) {
      const clickedProduct: EventProductOrBundle = this.trackingService.productToEventProduct(
        currentProduct,
        currentProductIndex
      );
      this.trackingService.sendEvent('productClick', clickedProduct);
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.crossSellComponent) {
      if (!this.componentIsSeen && this.scrollSpyService.isElementActive(this.crossSellComponent)) {
        this.componentIsSeen = true;
        if (this.currentProducts) {
          const showMore = this.showMoreDesktop && !this.isMobile;
          this.activateCrossSellProductListTracking({
            index: showMore ? 0 : this.currentSwiperInfo?.index,
            itemsPerView: showMore ? this.productsPerRow : this.currentSwiperInfo?.itemsPerView
          });
        }
      }
    }
  }

  activateCrossSellProductListTracking({ index, itemsPerView }: ISliderInfo): void {
    const updatedBundles = this.currentBundles
      .filter((bundle) =>
        bundle.products?.every(
          (product) =>
            product.price?.value !== null &&
            product.price?.value !== undefined &&
            product.onSale &&
            (product.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
              product.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK)
        )
      )
      .map((bundle) => {
        return {
          ...bundle,
          isBundle: true
        };
      });
    const updatedProducts = this.currentProducts.map((product) => {
      return {
        ...product,
        isBundle: false
      };
    });
    const products = [...updatedBundles, ...updatedProducts];
    const maxItems = (itemsPerView === 'auto') ? 3 : itemsPerView;
    const productsVisibles =
      this.showMoreDesktop && !this.isMobile
        ? products.slice(0, maxItems * (index + 1))
        : products.slice(index, maxItems + index);

    this.trackingService.crossSellProductListView(productsVisibles, products);
  }

  private loadProductsAndBundles(): void {
    const bundles$: Observable<Bundle[]> =
      this.codes.bundles?.length
        ? combineLatest(
          this.codes.bundles?.map((code) => this.bundleService.getBundle(code).pipe(
            catchError(() => of(null))
          ))
        ).pipe(map((bundles) => bundles.filter((b) => b !== null)))
        : of([]);

    const products$: Observable<Product[]> = this.codes.products?.length
      ? combineLatest(
        this.codes.products.map((code) =>
          this.productService.hasError(code, ProductScopeEnum.CARD)
            .pipe(
              switchMap((err) => {
                if (err) return of(null);
                return this.productService.get(code, ProductScopeEnum.CARD).pipe(first((p) => !!p));
              })
            )
        )
      ).pipe(
        map((products) => products.filter((product): product is Product => product !== null))
      )
      : of([]);

    const baseStore = this.baseStoreService.baseStore;

    combineLatest([bundles$, products$])
      .pipe(
        take(1),
        switchMap(([bundles, products]) => {
          const productCards$: Observable<IProductCard>[] = products
            .filter((product) => product?.price?.value > 0)
            .map((product) => {
              const updatedProduct = {
                ...product,
                brand: this.brandCorner ? this.brandCorner : product.brand ? product.brand : BrandsEnum.default,
                url: product?.isAccessory && this.productCodeForAccessory
                  ? product?.url + '?productCode=' + this.productCodeForAccessory
                  : product?.url
              };
              return this.productMapper.productToProductCard(updatedProduct);
            });

          const productCardsCombined$ = productCards$.length > 0
            ? combineLatest(productCards$)
            : of([]);

          const bundleCards$ = bundles
            .filter((bundle) =>
              bundle.products?.every(
                (product) =>
                  product.price?.value !== null &&
                  product.price?.value !== undefined &&
                  product.onSale &&
                  (product.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
                    product.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK)
              )
            )
            .map((bundle) => {
              const updatedBundle = {
                ...bundle,
                brand: this.brandCorner ? this.brandCorner : bundle.brand ? bundle.brand : BrandsEnum.default,
                isTaxesIncluded: baseStore.displayTaxesIncluded ? baseStore.displayTaxesIncluded : false
              };
              return this.bundleMapper.bundleToIProductCustomer(updatedBundle).pipe(
                map((bundleCard) => ({
                  ...bundleCard,
                  isProductComparator: false,
                  isBundle: true
                }))
              );
            });

          const bundleCardsCombined$ = bundleCards$.length > 0
            ? combineLatest(bundleCards$)
            : of([]);

          return combineLatest([
            of(bundles),
            of(products),
            productCardsCombined$.pipe(
              map((productCards) =>
                productCards.map((productCard) => ({
                  ...productCard,
                  isProductComparator: false,
                  isTaxesIncluded: baseStore.displayTaxesIncluded,
                  ratings: !baseStore.activateRatingAndReviews ? null : productCard.ratings,
                  isBundle: false
                }))
              )
            ),
            bundleCardsCombined$
          ]);
        }),
        catchError((err) => {
          console.error('Erreur dans loadProductsAndBundles', err);
          return of([[], []]);
        })
      )
      .subscribe(
        ([bundles, products, productCards, bundleCards]) => {
          this.isNewPriceManagementActivated = baseStore.activateNewPriceManagement;
          this.currentBundles = bundles;
          this.currentProducts = products;
          this.products = [...bundleCards, ...productCards];

          if (this.refreshPrices) {
            this.updateProductCards();
          } else {
            this.isSSR = false;
          }
          this.areProductsLoaded = true;
          this.hasLoaded.emit();
          this.cdRef.markForCheck();
        }
      );
  }

  updateProductCards() {
    if (isPlatformBrowser(this.platformId)) {
      const updateProductPrices$: Observable<IProductCard>[] = this.products
        .map((product: IProductCard) => {
          if (!product.isBundle) {
            return this.productService.get(product.code, [ProductScopeEnum.PRICE, ProductScopeEnum.STOCK]).pipe(
              filter((p) => !!p),
              map((productData) => {
                const formattedPrices = this.productMapper.formatCardPrice(productData, this.isNewPriceManagementActivated);
                return {
                  ...product,
                  price: formattedPrices?.price,
                  discountedPrice: formattedPrices?.discountedPrice,
                  recommendedPrice: formattedPrices?.recommendedPrice,
                  stock: {
                    ...product?.stock,
                    lowStock: this.stockService.checkStock(productData?.price?.value, productData?.stock?.stockLevel)
                  }
                };
              }),
              catchError((err) => {
                console.error(`Error retrieving price for ${product.code}`, err);
                return of(product);
              })
            );
          } else {
            return of(product);
          }
        });

      combineLatest(updateProductPrices$).pipe(
        take(1)
      ).subscribe((updatedProducts: IProductCard[]) => {
        const updatedProductsMap = new Map(updatedProducts.map((product) => [product.code, product]));
        this.products = this.products.map((product) => {
          if (updatedProductsMap.has(product.code)) {
            return { ...product, ...updatedProductsMap.get(product.code) };
          }
          return product;
        });
        this.isSSR = false;
        this.cdRef.markForCheck();
      });
    }
  }

  onReviewsSeeAllClicked(event: { productCode: string; productName: string }) {
    this.trackingService.sendEvent('clickSeeReviews', {
      productName: event.productName,
      productID: event.productCode
    });
  }

  openRecommendedPriceModal() {
    this.isOpenedRecommendedModal = true;
  }
}
